/// <amd-module name="Reporting/Visuals/LayoutComponents/Widgets/ChartComponent" />
import * as React from "react";
import { DemandType } from "Widgets/Demands/DemandType";
import { IDemandHandler } from "Widgets/Demands/IDemandHandler";

import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { Chart } from "VSS/Features/Charts/Chart";
import * as Chart_Contracts from "VSS/Features/Charts/Contracts";
import { VssComponent } from "VSS/Platform/Layout";

/**
 * Stateless parameters (creation-time) for constructing a ChartComponent
 */
export class ChartComponentProps {
    public chartOptions: Chart_Contracts.ChartProps;
}

export class ChartComponent extends VssComponent<ChartComponentProps, any> {
    public render() {
        return <Chart {...this.updateChartOptions({ ...this.props.chartOptions })} />;
    }

    /** Extensibility hook for derived type to modify chart options. */
    protected updateChartOptions(chartOptions: Chart_Contracts.ChartProps): Chart_Contracts.ChartProps {
        return chartOptions;
    }
}

/** Renders Chart as with ChartComponent, while overriding specific settings for presentational chrome to make the best of limited space:
 * 1- suppress axis labels & ticks.
 * 2- suppress legends.
 * 3- tooltips only show a compact single row.
 *  Any other options around these settings remain unmodified. */
export class ChromelessChartComponent extends ChartComponent {
    protected updateChartOptions(chartOptions: Chart_Contracts.ChartProps): Chart_Contracts.ChartProps {
        chartOptions.xAxis = chartOptions.xAxis || {};
        chartOptions.xAxis.labelsEnabled = false;
        chartOptions.xAxis.markingsEnabled = false;

        chartOptions.yAxis = chartOptions.yAxis || {};
        chartOptions.yAxis.labelsEnabled = false;
        chartOptions.yAxis.markingsEnabled = false;

        chartOptions.legend = chartOptions.legend || {};
        chartOptions.legend.enabled = false;

        chartOptions.tooltip = chartOptions.tooltip || {};
        chartOptions.tooltip.onlyShowFocusedSeries = true;

        return chartOptions;
    }
}

/** Renders Chart as with ChartComponent, while overriding specific settings for presentational chrome to make the best of limited space:
 * 1- suppress axis labels & ticks.
 * 2- suppress legends.
 * 3- tooltips not shown.
 *  Any other options around these settings remain unmodified. */
export class MinimalChromelessChartComponent extends ChartComponent {
    protected updateChartOptions(chartOptions: Chart_Contracts.ChartProps): Chart_Contracts.ChartProps {
        chartOptions.xAxis = {};
        chartOptions.xAxis.labelsEnabled = false;
        chartOptions.xAxis.markingsEnabled = false;
        chartOptions.xAxis.retainAxisLine = true;

        chartOptions.yAxis = {};
        chartOptions.yAxis.labelsEnabled = false;
        chartOptions.yAxis.markingsEnabled = false;
        chartOptions.yAxis.retainAxisLine = true;

        chartOptions.legend = {};
        chartOptions.legend.enabled = false;

        chartOptions.tooltip = {};
        chartOptions.tooltip.enabled = false;

        chartOptions.suppressMargin = true;
        chartOptions.suppressAnimation = true;
        return chartOptions;
    }
}

/** Responsible for rendering charts.
 * Allows for rendering a configured chart as "chromeless", meaning omission of axis labels/ticks, and legends.
 * Takes care of demand registration.
 */
export default class ChartComponentRenderer {
    public static registerDemands(demandHandler: IDemandHandler): void {
        demandHandler.registerDemand(DemandType.chart);
    }

    public static render(props: ChartComponentProps | undefined): JSX.Element {
        if (props) {
            return <ChartComponent {...props} />;
        } else {
            // This scenario shouldnt face a user if a consumer is blocking until they have a valid configuration.
            // The static sizing is our only choice since we have no props to work with
            return (
                <NoDataMessage
                    visualTitle={""}
                    size={{
                        height: 300,
                        width: 300
                    }}
                />
            );
        }
    }

    public static renderChromeless(props: ChartComponentProps | undefined): JSX.Element | null {
        return props != undefined ? <ChromelessChartComponent {...props} /> : null;
    }

    public static renderMinimalChromeless(props: ChartComponentProps | undefined): JSX.Element {
        if (props) {
            return <MinimalChromelessChartComponent {...props} />;
        } else {
            // This scenario shouldnt face a user if a consumer is blocking until they have a valid configuration.
            // The static sizing is our only choice since we have no props to work with
            return (
                <NoDataMessage
                    visualTitle={""}
                    size={{
                        height: 40,
                        width: 140
                    }}
                />
            );
        }
    }
}
